import { appColors } from '@base/useTheme';
import {
    AssignmentLabelRepresentation,
    FolderItemRepresentation,
    FolderItemType,
    ShareRepresentation,
    ShareRestrictionsValue,
    ShareSearchFilter,
    ShareStatus,
    ShareType,
} from '@common/api';
import { ContextMenu } from '@components/context-menu/ContextMenu';
import { ExpiresSelect } from '@components/expires-select/ExpiresSelect';
import { FilterDropdown } from '@components/filter-dropdown/FilterDropdown';
import { FilterMenuContent } from '@components/filter-menu/FilterMenuContent';
import { FlexRow, IsolateAbsolute, Relative } from '@components/layout/Flex';
import { Loading } from '@components/loading/Loading';
import { getStatusOptions } from '@components/Status';
import { Switch } from '@components/switch';
import {
    ListOption,
    TableSheet,
    TableSheetCellWidget,
    TableSheetColumnConfig,
    TableSheetColumnsConfig,
    TableSheetFilter,
    TableSheetIgnoreDrag,
    TableSheetIgnoreDragContainer,
    TableSheetMenuRenderProps,
    TableSheetRowsConfig,
    TableSheetUpdate,
    makeBooleanDataType,
    makeDateDataType,
    makeEnumDataType,
    makePopoverCell,
    makeRowAccessor,
    makeRowPatcher,
    makeStringDataType,
    normalizeSelection,
    useTableSheetStateLocal,
} from '@components/table-sheet';
import { TooltipWrapper } from '@components/tooltip/Tooltip';
import { Text } from '@components/typography/Typography';
import { useCreateLabel, useListLabels, useDeleteLabel, useUpdateLabel } from '@data/label/labelActions';
import { useSystemVersions } from '@data/entity/fixtures';
import { EDIT_OWNED_FOLDERS_ENABLED } from '@data/permission/dataEntryFolders';
import { shareHasExpirationTime, shareIsTracked } from '@data/share/shareActions';
import { Labels } from '@features/labels/Labels';
import { useI18nContext } from '@i18n/i18n-react';
import { TranslationFunctions } from '@i18n/i18n-types';
import { useFormatting } from '@providers/FormattingProvider';
import { $delete, Update } from '@use-gpu/state';
import { toMap } from '@util/array';
import { ITEM_ICONS, getFolderItemTypeSingular } from '@util/folderItems';
import { FC, useLayoutEffect, useMemo } from 'react';

import {
    PUBLIC_LINK_KEY,
    getPublicLinkOption,
    getUserGroupKey,
    getUserKey,
    parseUserAndGroupKeys,
    useUserAndGroupOptions,
} from './hooks/useUserAndGroupOptions';
import { parseUserGroupKey } from './hooks/useUserGroupOptions';
import { parseUserKey } from './hooks/useUserOptions';
import { ShareAvatars, formatShareUsersPlainText } from './ShareAvatars';
import { ShareRestrictionLabel, ShareRestrictionOptions, formatShareRestrictionLabel } from './ShareRestrictions';
import { makeShareStatusCell, formatAssignmentStatus } from './ShareStatus';
import { ShareTableActions } from './ShareTableActions';
import { ShareFilter } from './types';

const NO_VALUE = 'null';
const MAX_ITEMS = 10;
const POPOVER_OPTIONS = { unstyled: true, $zIndex: 1200 };

const cellStyle = <T,>(row: T, rowIndex: number, readOnly: boolean) =>
    readOnly ? { background: appColors.ui.disabled } : undefined;

const getShareTypeOptions = (LL: TranslationFunctions): ListOption<ShareType>[] => [
    { label: LL.VIEW_ONLY(), value: ShareType.View },
    { label: LL.DATA_EDIT(), value: ShareType.DataEntry },
    { label: LL.FULL_EDIT(), value: ShareType.Edit },
];

const getRestrictionKey = (r?: ShareRestrictionsValue | null) =>
    r
        ? `${r.intervalStart}-${r.intervalEnd}-${r.versionIds.sort((a, b) => a - b).join(',')}-${
              r.allowEntryOnPropertyColumns
          }`
        : NO_VALUE;

export type ShareTableProps = {
    hasTracked?: boolean;
    hasUntracked?: boolean;
    isGlobal?: boolean;
    isSheet?: boolean;
    readOnly?: boolean;

    searchFilter: ShareFilter;
    isLoading?: boolean;
    jumpToRow?: number | null;

    filter: ShareSearchFilter;
    onFilter: (update: Update<ShareSearchFilter>) => void;

    shares?: ShareRepresentation[];
    filteredShares?: ShareRepresentation[];
    onSheetChange: (ops: TableSheetUpdate<ShareRepresentation, Update<ShareRepresentation>>[]) => void;
    onLoadMore?: () => void;
    canLoadMore?: boolean;
};

export const ShareTable: FC<ShareTableProps> = (props: ShareTableProps) => {
    const {
        hasTracked = true,
        hasUntracked = true,
        isGlobal,
        isSheet,
        readOnly = false,
        searchFilter,
        jumpToRow,
        shares,
        filteredShares,
        isLoading,
        onSheetChange,
        filter,
        onFilter,
        onLoadMore,
        canLoadMore,
    } = props;

    const { LL } = useI18nContext();
    const formatting = useFormatting();

    const { data: labels } = useListLabels();
    const { allLabels, labelById } = useMemo(() => {
        const allLabels = labels ?? [];
        const labelById = toMap(allLabels, 'id');
        return { allLabels, labelById };
    }, [labels]);
    const { mutateAsync: updateLabel } = useUpdateLabel();
    const { mutateAsync: createLabel } = useCreateLabel();
    const { mutateAsync: deleteLabel } = useDeleteLabel();

    const { usersMap, userGroupsMap, allOptions } = useUserAndGroupOptions({ publicLink: true });
    const { allOptions: userAndGroupOptions } = useUserAndGroupOptions();
    const versions = useSystemVersions();

    const isTrackedOnly = hasTracked && !hasUntracked;
    const rows = useMemo(
        () =>
            filteredShares?.filter((share) => {
                const { userIds, userGroupIds, publicLink } = searchFilter;
                const uids = userIds.length ? new Set(userIds) : null;
                const gids = userGroupIds.length ? new Set(userGroupIds) : null;

                return (
                    (!uids || share.userIds.some((id) => uids.has(id))) &&
                    (!gids || share.userGroupIds.some((id) => gids.has(id))) &&
                    (!publicLink || share.publicKey.active)
                );
            }) ?? [],
        [filteredShares, searchFilter]
    );

    const filterData = useMemo(() => {
        const seenRestrictionKey = new Set();

        const activeOptions = [
            { value: 1, label: LL.YES() },
            { value: 0, label: LL.NO() },
        ];

        const folderItemOptions = shares?.map((s) => ({ value: s.itemNode.id, label: s.itemNode.name })) ?? [];

        const seenItemNodeType = new Set<FolderItemType>();
        const folderItemTypeOptions =
            shares?.flatMap((s) => {
                const type = s.itemNode.type;
                if (seenItemNodeType.has(type)) return [];
                seenItemNodeType.add(type);
                return [{ value: type, label: getFolderItemTypeSingular(type, LL) }];
            }) ?? [];

        const labelOptions = allLabels.map((l) => ({
            value: l.id,
            label: l.name,
        }));

        const restrictionOptions =
            shares?.flatMap((s) => {
                const {
                    restrictions: { active, value },
                } = s;
                if (!active) return [];

                const key = getRestrictionKey(active ? value : null);

                seenRestrictionKey.add(key);
                return [
                    {
                        value: key,
                        label: formatShareRestrictionLabel(s.restrictions, versions, LL.PROPERTY_COLUMNS()),
                        obj: value,
                    },
                ];
            }) ?? [];
        const expirationDateOptions =
            shares
                ?.filter((s) => s.expirationTime.active)
                .map((s) => ({
                    label: formatting.formatLocalDate(s.expirationTime.value),
                    value: formatting.formatLocalDate(s.expirationTime.value, 'yyyy-mm-dd'),
                })) ?? [];
        const typeOptions = getShareTypeOptions(LL);
        const statusOptions = getStatusOptions(LL).filter((o) => o.value !== ShareStatus.InProgress);

        const commentOptions =
            shares
                ?.filter((s) => s.comment?.length)
                .map((s) => ({
                    label: s.comment!,
                    value: s.comment!,
                })) ?? [];

        return {
            activeOptions,
            folderItemOptions,
            folderItemTypeOptions,
            labelOptions,
            restrictionOptions,
            expirationDateOptions,
            typeOptions,
            statusOptions,
            commentOptions,
        };
    }, [shares, formatting, versions, labelById, LL]);

    const {
        activeOptions,
        folderItemOptions,
        folderItemTypeOptions,
        labelOptions,
        restrictionOptions,
        expirationDateOptions,
        typeOptions,
        statusOptions,
        commentOptions,
    } = filterData;

    const { selectedRestrictions, selectedAssignees, selectedApprovers } = useMemo(() => {
        const selectedRestrictions = {
            ...filter.restrictions,
            values: filter.restrictions?.values?.map(getRestrictionKey),
        };

        const selectedAssignees =
            filter.assignees === undefined
                ? {} // leave "values" as undefined as well to keep initial state
                : {
                      ...filter.assignees,
                      values: [
                          ...(filter.assignees.values?.userGroupIds?.map(getUserGroupKey) ?? []),
                          ...(filter.assignees.values?.userIds?.map(getUserKey) ?? []),
                          ...(filter.assignees.publicKey ? [PUBLIC_LINK_KEY] : []),
                      ],
                  };

        const selectedApprovers =
            filter.approvers === undefined
                ? {} // leave "values" as undefined as well to keep initial state
                : {
                      ...filter.approvers,
                      values: [
                          ...(filter.approvers?.values?.userGroupIds?.map(getUserGroupKey) ?? []),
                          ...(filter.approvers?.values?.userIds?.map(getUserKey) ?? []),
                      ],
                  };

        return { selectedRestrictions, selectedAssignees, selectedApprovers };
    }, [filter]);

    const columns = useMemo(() => {
        const timeType = makeDateDataType(formatting, 'timestamp');
        const columns: TableSheetColumnConfig<ShareRepresentation, Update<ShareRepresentation>>[] = [];

        columns.push({
            key: 'active',
            header: {
                label: (
                    <TableSheetFilter
                        title={LL.ACTIVE()}
                        options={activeOptions}
                        selectAll={filter.active === undefined}
                        values={filter.active === undefined ? [1, 0] : filter.active ? [1] : [0]}
                        canSelectBlanks={false}
                        onChange={(active) => {
                            if (
                                active.selectAll ||
                                (active.values.includes(1) && active.values.includes(0)) ||
                                active.values.length === 0
                            ) {
                                onFilter({ active: $delete() });
                            } else if (active.values.includes(1)) {
                                onFilter({ active: true });
                            } else if (active.values.includes(0)) {
                                onFilter({ active: false });
                            }
                        }}
                    />
                ),
                width: 75,
            },
            type: makeBooleanDataType(),
            data: {
                ...makeRowAccessor('active'),
                readonly: (share) => shareHasExpirationTime(share) || shareIsTracked(share),
                style: cellStyle,
            },
            cell: {
                render: ({ row: share, value, disabled, onChange }) => {
                    const toggle = (
                        <Switch
                            checked={value}
                            onChange={onChange ? (e) => onChange(e.target.checked) : () => {}}
                            disabled={disabled}
                        />
                    );

                    const tooltip = shareHasExpirationTime(share)
                        ? LL.CANNOT_EDIT_WHEN_TIME_LIMIT()
                        : shareIsTracked(share)
                        ? LL.CANNOT_EDIT_WHEN_TRACKED()
                        : null;

                    const view = tooltip ? <TooltipWrapper label={tooltip}>{toggle}</TooltipWrapper> : toggle;

                    return <TableSheetCellWidget>{view}</TableSheetCellWidget>;
                },
                action: ({ value, onChange }) => {
                    onChange?.(!value);
                    return true;
                },
            },
        });

        if (isGlobal) {
            columns.push({
                key: 'itemType',
                header: {
                    label: (
                        <TableSheetFilter
                            title={LL.ITEM_TYPE()}
                            options={folderItemTypeOptions}
                            menuWidth={200}
                            {...filter.itemNodeTypes}
                            canSelectBlanks={false}
                            onChange={(itemNodeTypes) => onFilter({ itemNodeTypes })}
                        />
                    ),
                    width: 130,
                },
                data: {
                    ...makeRowPatcher(),
                    readonly: () => true,
                },
                type: {
                    parse: () => ({}),
                    format: (share: ShareRepresentation) => getFolderItemTypeSingular(share.itemNode.type, LL),
                    display: (share: ShareRepresentation) => (
                        <FlexRow>
                            <Relative style={{ top: -2 }}>{ITEM_ICONS[share.itemNode.type]}</Relative>
                            <span>{getFolderItemTypeSingular(share.itemNode.type, LL)}</span>
                        </FlexRow>
                    ),
                },
            });
            columns.push({
                key: 'item',
                header: {
                    label: (
                        <TableSheetFilter
                            title={LL.ITEM()}
                            options={folderItemOptions}
                            menuWidth={250}
                            {...filter.itemNodeIds}
                            canSelectBlanks={false}
                            onChange={(itemNodeIds) => onFilter({ itemNodeIds })}
                        />
                    ),
                    width: 150,
                },
                data: {
                    ...makeRowPatcher(),
                    readonly: () => true,
                },
                type: {
                    // Don't accept text (from plain-text clipboard)
                    parse: () => ({}),
                    // Format as text (for plain-text clipboard)
                    format: (share: ShareRepresentation) => share.itemNode.name,
                    // Format as HTML (for display)
                    display: (share: ShareRepresentation) => <span>{share.itemNode.name}</span>,
                },
            });
        }

        columns.push(
            {
                key: 'assignees',
                header: {
                    label: (
                        <TableSheetFilter
                            title={LL.USER_OR_GROUP()}
                            options={allOptions}
                            {...selectedAssignees}
                            onChange={(assignees) => {
                                const values = parseUserAndGroupKeys(assignees.values);
                                onFilter({
                                    assignees: {
                                        ...assignees,
                                        publicKey: assignees.selectAll || values.publicLink,
                                        values: {
                                            userIds: values.userIds,
                                            userGroupIds: values.userGroupIds,
                                        },
                                    },
                                });
                            }}
                        />
                    ),
                    width: 150,
                },
                data: makeRowPatcher(),
                type: {
                    // Don't accept text (from plain-text clipboard)
                    parse: () => ({
                        userIds: [],
                        userGroupIds: [],
                        publicKey: { active: false, value: '00000000-0000-0000-0000-000000000000' },
                    }),
                    // Format as text (for plain-text clipboard)
                    format: (share: ShareRepresentation) =>
                        formatShareUsersPlainText(share.userIds, share.userGroupIds, usersMap, userGroupsMap, LL) ??
                        LL.PUBLIC(),
                    // Format as HTML (for display)
                    display: (share: ShareRepresentation) => (
                        <ShareAvatars
                            userIds={share.userIds}
                            userGroupIds={share.userGroupIds}
                            publicKey={share.publicKey.active}
                            placeholder={LL.NONE_SELECTED()}
                            limit={3}
                        />
                    ),
                },
                clipboard: {
                    format: ({ userIds, userGroupIds }) => ({ userIds, userGroupIds }),
                },
                cell: makePopoverCell(({ value: share, onChange }) => {
                    const selected = [
                        ...share.userIds.map((id: number) => ({ value: getUserKey(id) })),
                        ...share.userGroupIds.map((id: number) => ({ value: getUserGroupKey(id) })),
                    ];
                    if (share.publicKey.active) selected.unshift(getPublicLinkOption(LL));

                    const isSheet = share.itemNode.type === FolderItemType.Sheet;

                    return (
                        <FilterMenuContent
                            searchable
                            multiselect
                            maxItems={MAX_ITEMS}
                            getLabel={(o) => o.label}
                            getValue={(o) => o.value}
                            options={isSheet ? allOptions : userAndGroupOptions}
                            selected={selected}
                            menuWidth={240}
                            onSelect={(options) => {
                                const publicOption = options.find((o) => o.value === PUBLIC_LINK_KEY);

                                // Row patcher is applied implicitly via `data`, only return changed fields.
                                onChange?.({
                                    publicKey: { active: publicOption !== undefined },
                                    ...parseUserAndGroupKeys(options.map((o) => o.value)),
                                });
                            }}
                        />
                    );
                }, POPOVER_OPTIONS),
            },
            {
                key: 'type',
                header: {
                    label: (
                        <TableSheetFilter
                            title={LL.TYPE()}
                            options={typeOptions}
                            {...filter.types}
                            canSelectBlanks={false}
                            onChange={(types) => onFilter({ types })}
                        />
                    ),
                    width: 105,
                },
                data: {
                    ...makeRowAccessor('type'),
                    readonly: (share) => shareIsTracked(share),
                    style: cellStyle,
                },
                type: makeEnumDataType(getShareTypeOptions(LL), true, null),
                cell: {
                    render: ({ value, disabled, onChange, type, row }) => {
                        const itemNode = row.itemNode;
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        let options: ListOption<any>[] = type?.options?.() ?? [];
                        if (!supportsDataEntryShare(itemNode)) {
                            options = options.filter((o) => o.value !== ShareType.DataEntry);
                        } else if (EDIT_OWNED_FOLDERS_ENABLED && itemNode.type === FolderItemType.Folder) {
                            // On folders the DATA_ENTRY tier is "Edit owned": create/edit your own
                            // items inside without permission to modify the folder itself.
                            options = options.map((o) =>
                                o.value === ShareType.DataEntry ? { ...o, label: LL.EDIT_OWNED() } : o
                            );
                        }
                        const selectedOption = options.find((o) => o.value === value);

                        return (
                            <TableSheetCellWidget>
                                <TableSheetIgnoreDrag>
                                    <FilterDropdown
                                        menuWidth={120}
                                        menuOffset={-8}
                                        maxItems={options.length}
                                        title={selectedOption?.label ?? ''}
                                        options={options}
                                        selected={selectedOption ? [selectedOption] : []}
                                        onSelect={(options) => onChange?.(options[0].value)}
                                        onRemove={() => onChange?.(null)}
                                        getValue={(option) => option.value}
                                        getLabel={(option) => option.label}
                                        placement="bottom-start"
                                        removeable={false}
                                        disabled={disabled}
                                    />
                                </TableSheetIgnoreDrag>
                            </TableSheetCellWidget>
                        );
                    },
                },
            },
            {
                key: 'time',
                header: {
                    label: (
                        <TableSheetFilter
                            title={LL.TIME_LIMIT()}
                            options={expirationDateOptions}
                            {...filter.expirationDates}
                            onChange={(expirationDates) => onFilter({ expirationDates })}
                        />
                    ),
                    width: 150,
                },
                data: makeRowAccessor('expirationTime'),
                type: {
                    parse: (value, prev) => {
                        const parsed = timeType.parse(value);
                        return { active: !!parsed, value: parsed ?? prev.value };
                    },
                    format: (value) => (value.active ? timeType.format(value.value) : ''),
                    display: (value) =>
                        value.active ? (
                            <TableSheetCellWidget>
                                <Text
                                    style={{
                                        color: value.value < Date.now() ? appColors.error.w500 : undefined,
                                    }}
                                >
                                    {timeType.format(value.value)}
                                </Text>
                            </TableSheetCellWidget>
                        ) : null,
                },
                cell: makePopoverCell(({ value, onChange }) => (
                    <FlexRow $width={250}>
                        <ExpiresSelect width="100%" value={value} onChange={(d) => onChange?.(d ?? null)} />
                    </FlexRow>
                )),
            }
        );

        if (isGlobal || isSheet) {
            // add special columns that are only relevant to sheets
            columns.push({
                key: 'restrictions',
                header: {
                    label: (
                        <TableSheetFilter
                            title={LL.RESTRICTIONS()}
                            options={restrictionOptions}
                            menuWidth={220}
                            {...selectedRestrictions}
                            onChange={(restrictions) =>
                                onFilter({
                                    restrictions: {
                                        ...restrictions,
                                        values: restrictionOptions
                                            .filter((r) => r.obj && restrictions.values.includes(r.value))
                                            .map((r) => r.obj!),
                                    },
                                })
                            }
                        />
                    ),
                    width: 150,
                },
                data: {
                    ...makeRowAccessor('restrictions'),
                    readonly: (share) => share.itemNode.type !== FolderItemType.Sheet,
                    style: cellStyle,
                },
                type: {
                    // Don't accept text (from plain-text clipboard)
                    parse: (_, prev) => ({ ...prev, active: false }),
                    // Format as text (for plain-text clipboard)
                    format: (restrictions) =>
                        restrictions && formatShareRestrictionLabel(restrictions, versions, LL.PROPERTY_COLUMNS()),
                    // Format as HTML (for display)
                    display: (restrictions) => restrictions && <ShareRestrictionLabel restrictions={restrictions} />,
                },
                clipboard: {
                    // Clipboard payload is raw restrictions object
                    format: (restrictions) => restrictions,
                },
                cell: makePopoverCell((props) => <ShareRestrictionOptions {...props} />),
            });

            if (hasTracked)
                columns.push(
                    {
                        key: 'status',
                        header: {
                            label: (
                                <TableSheetFilter
                                    title={LL.STATUS()}
                                    {...filter.statuses}
                                    options={statusOptions}
                                    canSelectBlanks={!isTrackedOnly}
                                    onChange={(statuses) => onFilter({ statuses: statuses })}
                                />
                            ),
                            width: 200,
                        },
                        data: {
                            ...makeRowPatcher(),
                            readonly: (share) => share.itemNode.type !== FolderItemType.Sheet,
                            style: cellStyle,
                        },
                        type: {
                            parse: () => ({
                                status: null,
                                approverUserId: null,
                                approverUserGroupId: null,
                            }),
                            format: (share) => share.status,
                            display: (share) => formatAssignmentStatus(share),
                        },
                        clipboard: {
                            format: ({ status, approverUserId, approverUserGroupId }) => ({
                                status,
                                approverUserId,
                                approverUserGroupId,
                            }),
                        },
                        cell: makeShareStatusCell(),
                    },
                    {
                        key: 'approver',
                        header: {
                            label: (
                                <TableSheetFilter
                                    title={LL.APPROVER()}
                                    options={allOptions}
                                    {...selectedApprovers}
                                    onChange={(approvers) => {
                                        const values = parseUserAndGroupKeys(approvers.values);
                                        onFilter({
                                            approvers: {
                                                ...approvers,
                                                values,
                                            },
                                        });
                                    }}
                                />
                            ),
                            width: 150,
                        },
                        data: {
                            ...makeRowPatcher(),
                            readonly: (share) => share.itemNode.type !== FolderItemType.Sheet,
                            style: cellStyle,
                        },
                        type: {
                            parse: () => ({
                                status: null,
                                approverUserId: null,
                                approverUserGroupId: null,
                            }),
                            format: (share) => {
                                return formatShareUsersPlainText(
                                    share.approverUserId && [share.approverUserId],
                                    share.approverUserGroupId && [share.approverUserGroupId],
                                    usersMap,
                                    userGroupsMap,
                                    LL
                                );
                            },
                            display: (share: ShareRepresentation) => (
                                <ShareAvatars
                                    userIds={share.approverUserId ? [share.approverUserId] : []}
                                    userGroupIds={share.approverUserGroupId ? [share.approverUserGroupId] : []}
                                />
                            ),
                        },
                        clipboard: {
                            format: ({ status, approverUserId, approverUserGroupId }) => ({
                                status,
                                approverUserId,
                                approverUserGroupId,
                            }),
                        },
                        cell: makePopoverCell(({ value, onChange }) => {
                            const selected = [];
                            if (value.approverUserId)
                                selected.push({ value: getUserKey(value.approverUserId), label: '' });
                            if (value.approverUserGroupId)
                                selected.push({ value: getUserGroupKey(value.approverUserGroupId), label: '' });
                            return (
                                <FilterMenuContent
                                    searchable
                                    maxItems={MAX_ITEMS}
                                    options={userAndGroupOptions}
                                    getLabel={(o) => o.label}
                                    getValue={(o) => o.value}
                                    selected={selected}
                                    onSelect={(options) => {
                                        const userId = parseUserKey(options[0].value);
                                        const groupId = parseUserGroupKey(options[0].value);
                                        // Row patcher is applied implicitly via `data`, only return changed fields.
                                        onChange?.({
                                            status: value.status ?? ShareStatus.Shared,
                                            approverUserId: userId,
                                            approverUserGroupId: groupId,
                                        });
                                    }}
                                />
                            );
                        }, POPOVER_OPTIONS),
                    }
                );
        }

        // add last two columns: labels and comment
        columns.push(
            {
                key: 'labels',
                header: {
                    label: labelOptions.length ? (
                        <TableSheetFilter
                            title={LL.LABELS()}
                            options={labelOptions}
                            {...filter.labelIds}
                            onChange={(labelIds) => onFilter({ labelIds })}
                        />
                    ) : (
                        LL.LABELS()
                    ),
                    width: 300,
                },
                data: makeRowAccessor('labelIds'),
                type: {
                    // Don't accept text (from plain-text clipboard)
                    parse: () => [],
                    // Format as text (for plain-text clipboard)
                    format: (value) =>
                        allLabels
                            .filter((label) => value.includes(label.id))
                            .map((label) => label.name)
                            .join(', '),
                },
                clipboard: {
                    // Clipboard payload is label ids
                    format: (labelIds) => labelIds,
                },
                cell: {
                    render: ({ value, onChange }) => (
                        <FlexRow>
                            <TableSheetIgnoreDragContainer>
                                <Labels
                                    readonly={readOnly}
                                    allLabels={allLabels}
                                    colorStrategy="picker"
                                    labels={allLabels.filter((label) => value.includes(label.id))}
                                    getLabelName={(label: AssignmentLabelRepresentation) => label.name}
                                    onRemove={(label) => onChange?.(value.filter((id: number) => id !== label.id))}
                                    onAdd={(label) => onChange?.([...value, label.id])}
                                    onUpdate={(_, updatedLabel) => updateLabel(updatedLabel)}
                                    onCreate={(name, hexColor) => createLabel({ name, hexColor })}
                                    onDelete={(label) => deleteLabel(label.id)}
                                />
                            </TableSheetIgnoreDragContainer>
                        </FlexRow>
                    ),
                },
            },
            {
                key: 'comment',
                header: {
                    label: (
                        <TableSheetFilter
                            title={LL.COMMENT()}
                            options={commentOptions}
                            {...filter.comments}
                            onChange={(comments) => onFilter({ comments })}
                        />
                    ),
                    width: 400,
                },
                data: makeRowAccessor('comment'),
                type: makeStringDataType(),
            }
        );

        if (readOnly) {
            // view-only mode: user can see all shares on the item but cannot change them
            return columns.map((column) => ({
                ...column,
                data: { ...column.data, readonly: () => true, style: undefined },
            }));
        }

        return columns;
    }, [
        readOnly,
        activeOptions,
        commentOptions,
        createLabel,
        deleteLabel,
        expirationDateOptions,
        folderItemOptions,
        folderItemTypeOptions,
        hasTracked,
        labelOptions,
        onFilter,
        restrictionOptions,
        selectedApprovers,
        selectedAssignees,
        selectedRestrictions,
        statusOptions,
        typeOptions,
        updateLabel,
        userAndGroupOptions,
        versions,
        isTrackedOnly,
        isGlobal,
        isSheet,
        formatting,
        allLabels,
        usersMap,
        userGroupsMap,
        allOptions,
        LL,
        filter,
    ]);

    // Rows and columns config
    const tableRows: TableSheetRowsConfig<ShareRepresentation> = useMemo(
        () => ({
            count: rows.length,
            key: (i: number) => rows[i]?.id ?? i,
            get: (i: number) => rows[i],
            size: () => 32,
        }),
        [rows]
    );

    const logRowCount = Math.max(0, Math.floor(Math.log10(rows.length)) - 1);
    const tableColumns: TableSheetColumnsConfig<ShareRepresentation, Update<ShareRepresentation>> = useMemo(
        () => ({
            count: columns.length,
            key: (i: number) => columns[i]?.key ?? i,
            get: (i: number) => columns[i],
            size: (i: number) => columns[i]?.header.width ?? 34 + logRowCount * 10,
        }),
        [columns, logRowCount]
    );

    const tableSheetState = useTableSheetStateLocal({
        rows: tableRows,
        columns: tableColumns,
        configKey: 'share-table-v2',
        onSheetChange,
    });

    const renderOverlay = () =>
        rows.length === 0 ? (
            <IsolateAbsolute style={{ pointerEvents: 'none' }}>
                <FlexRow $justify="center" style={{ marginTop: '100px' }}>
                    {isLoading ? <Loading /> : LL.NO_RESULTS_FOUND()}
                </FlexRow>
            </IsolateAbsolute>
        ) : null;

    useLayoutEffect(() => {
        if (jumpToRow == null) return;

        const { selection, onSelectionChange } = tableSheetState;
        const [anchor, head] = selection ?? [
            [0, 0],
            [0, 0],
        ];

        onSelectionChange([
            [anchor[0], jumpToRow],
            [head[0], jumpToRow],
        ]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jumpToRow]);

    const { selection } = tableSheetState;
    const renderContextMenu = (props: TableSheetMenuRenderProps) => {
        if (!selection) return null;

        const { rowOrder } = tableSheetState;
        const [[, start], [, end]] = normalizeSelection(selection);

        const items = rowOrder
            .slice(start, end + 1)
            .map((rowIndex) => rows[rowIndex])
            .filter(Boolean);

        if (items.length === 0) return null;

        const areFolders = items.every((item) => item.itemNode.type === FolderItemType.Folder);
        const areSheets = items.every((item) => item.itemNode.type === FolderItemType.Sheet);

        const handleChange = (updates: Update<ShareRepresentation>[]) => {
            onSheetChange(
                items.map((item, idx) => ({
                    row: item,
                    rowIndex: start + idx,
                    updates: [updates[idx]],
                }))
            );
        };
        return (
            <ContextMenu {...props}>
                <ShareTableActions
                    items={items} // pass full array now
                    onChange={handleChange}
                    areFolders={areFolders}
                    areSheets={areSheets}
                    isGlobal={isGlobal}
                />
            </ContextMenu>
        );
    };

    return (
        <TableSheet
            {...tableSheetState}
            renderOverlay={renderOverlay}
            fill={[false, true]}
            negative={[1, 1]}
            sticky={isGlobal ? [4, 0] : [2, 0]}
            minCellSize={[20, 32]}
            onRowResize={undefined}
            contextMenu={readOnly ? undefined : renderContextMenu}
            canLoadMore={canLoadMore}
            onLoadMore={onLoadMore}
        />
    );
};
function supportsDataEntryShare(itemNode: FolderItemRepresentation) {
    const itemNodeType = itemNode.type;
    if (itemNodeType === FolderItemType.Sheet || itemNodeType === FolderItemType.Dashboard) {
        return true;
    }
    if (!EDIT_OWNED_FOLDERS_ENABLED) {
        // Legacy: DATA_ENTRY only on sheets, dashboards and folders configured to hold them.
        return (
            itemNodeType === FolderItemType.Folder &&
            (itemNode.allowedTypes.includes(FolderItemType.Sheet) ||
                itemNode.allowedTypes.includes(FolderItemType.Dashboard))
        );
    }
    return itemNodeType === FolderItemType.Folder;
}
